"use client";

import ExactConverter, {
  type ExactUnit,
} from "@/components/tools/calendars/exact-converter";
import { useToolUi } from "@/components/tools/tool-ui";
import { ENERGY_UNITS } from "@/lib/tool-kit/calendars/energy-units";

/** English strings. A translated content file overrides these through `ui`. */
export const ENERGY_UNIT_CONVERTER_STRINGS = {
  valueLabel: "Energy",
  valueHint:
    "A number such as 2000, 0.5 or 1.6e-19. Results are exact, using the NIST definitions of each unit.",
  fromLabel: "From",
  toLabel: "To",
  digitsLabel: "Significant digits",
  swap: "Swap units",
  resultHeading: "Result",
  plainLabel: "Converted value",
  scientificLabel: "Scientific notation",
  factorLabel: "Conversion factor",
  factorLine: "1 {from} = {factor} {to}",
  exact:
    "Exact: both units are defined by exact decimal factors, so nothing was rounded.",
  rounded: "Rounded to {digits} significant digits.",
  tableHeading: "Your energy in every unit",
  unitColumn: "Unit",
  valueColumn: "Value",
  enterPrompt: "Enter an amount of energy to convert.",
  invalidNumber:
    "That is not a number. Use digits with an optional decimal point and exponent, for example 2000 or 1.6e-19.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{value} {from} = {result} {to}",
  unitNames: {
    j: "joule (J)",
    kj: "kilojoule (kJ)",
    mj: "megajoule (MJ)",
    wh: "watt-hour (Wh)",
    kwh: "kilowatt-hour (kWh)",
    calth: "calorie, thermochemical (cal th)",
    kcalth: "kilocalorie, thermochemical (kcal th)",
    calit: "calorie, International Table (cal IT)",
    kcalit: "kilocalorie, International Table (kcal IT)",
    btuit: "British thermal unit, International Table (BTU IT)",
    btuth: "British thermal unit, thermochemical (BTU th)",
    ev: "electronvolt (eV)",
    kev: "kiloelectronvolt (keV)",
    mev: "megaelectronvolt (MeV)",
    erg: "erg",
    ftlbf: "foot-pound force (ft·lbf)",
    latm: "litre-atmosphere (L·atm)",
    tnt: "tonne of TNT (t TNT)",
  },
  calorieNote:
    "Which calorie? Nutrition tables conventionally use the thermochemical kilocalorie, 4.184 kJ; the International Table calorie is 4.1868 J and is about 0.07% larger. Food-label Calories are kilocalories.",
} as const;

export default function EnergyUnitConverterWidget() {
  const strings = useToolUi(ENERGY_UNIT_CONVERTER_STRINGS);
  const units: ExactUnit[] = ENERGY_UNITS.map((unit) => ({
    id: unit.id,
    symbol: unit.symbol,
    factor: unit.joules,
  }));
  return (
    <ExactConverter
      defaults={{ value: "2000", from: "kcalth", to: "kj" }}
      idPrefix="eu"
      notes={[strings.calorieNote]}
      strings={strings}
      units={units}
    />
  );
}
